import { Component, ViewChild } from '@angular/core';
import { PLUGINS, THEMES, TOOLS } from 'src/app/data/product/product.data';
import { BRAND } from '../../../../data/brand/brand.data';
import { URLS } from '../../../../data/navigation/navigation.data';
import { buildAppearanceSections } from '../../../../data/theme/appearance-menu';
import { DarkLightSettings } from '../../../../data/theme/theme.data';
import { Product, ProductLine } from '../../../../model/product/product.model';
import { AnalyticsService } from '../../../../service/analytics/analytics.service';
import { LoadingService } from '../../../../service/loading/loading.service';
import { RoutingService } from '../../../../service/routing/routing.service';
import { ThemeService } from '../../../../service/theme/theme.service';
import { DropdownCoordinatorService } from '../../../dropdown-menu/dropdown-coordinator.service';
import { DropdownMenuComponent, DropdownMenuSection } from '../../../dropdown-menu/dropdown-menu.component';

@Component( {
  selector: 'app-landing-layout-top-nav',
  templateUrl: './landing-layout-top-nav.component.html',
  styleUrls: [ './landing-layout-top-nav.component.scss' ]
} )
export class LandingLayoutTopNavComponent {
  loading = false;

  URLS = URLS;
  BRAND = BRAND;

  PLUGINS: ProductLine[] = PLUGINS;
  THEMES: ProductLine[] = THEMES;
  TOOLS: ProductLine[] = TOOLS;

  currentTheme: string = DarkLightSettings.auto;
  currentProductKey: string | null = null;

  // The Appearance menu keeps itself open on selection (closeOnSelect=false),
  // modes and themes alike, so looks can be tried in a row (owner 2026-10-16).
  @ViewChild( 'dropdownMenuTheme' ) appearanceMenu?: DropdownMenuComponent;

  constructor(
    private loadingService: LoadingService,
    private routingService: RoutingService,
    private themeService: ThemeService,
    private coordinator: DropdownCoordinatorService,
    private analyticsService: AnalyticsService,
  ) {
    this.loadingService.sharedLoading.subscribe( loading => this.loading = loading );
    this.themeService.darkLightSetting.subscribe(
      value => this.currentTheme = value || DarkLightSettings.auto
    );
    this.themeService.productTheme.subscribe(
      productTheme => this.currentProductKey = productTheme ? productTheme.key : null
    );
  }

  private cachedSections: DropdownMenuSection[] = [];
  private sectionsCacheKey = '';

  // Memoized so the getter returns a STABLE array reference across change
  // detection (rebuilding only when the mode or selected theme actually
  // changes). Without this every CD cycle handed the dropdown a fresh array,
  // churning the DOM and breaking hover.
  get settingsSections(): DropdownMenuSection[] {
    const key = this.currentTheme + '|' + this.currentProductKey;
    if ( key !== this.sectionsCacheKey ) {
      this.sectionsCacheKey = key;
      this.cachedSections = buildAppearanceSections( this.currentTheme, this.currentProductKey );
    }
    return this.cachedSections;
  }

  // Logo of the currently selected theme (shown next to "Appearance"); null on
  // the default palette.
  get currentProductLogo(): string | null {
    if ( !this.currentProductKey ) {
      return null;
    }
    return this.findThemeProduct( this.currentProductKey )?.logo ?? null;
  }

  // On the default palette (no theme logo), the active mode's glyph is shown
  // next to "Appearance" instead. Maps the dark/light setting to the moon variant.
  get currentModeVariant(): string {
    if ( this.currentTheme === DarkLightSettings.light ) {
      return 'light';
    }
    if ( this.currentTheme === DarkLightSettings.dark ) {
      return 'dark';
    }
    return 'both';
  }

  onSettingSelect( value: string ) {
    // Track appearance interactions (dark/light/auto mode, a product-theme
    // pick, or reset to default) so the dashboard shows how many visitors
    // change the look AND exactly what they picked. `selection` is the readable
    // choice to break down by in Mixpanel (Light / Dark / Auto / Default /
    // theme name); `kind` splits mode toggles from theme picks; `source`
    // distinguishes the top nav from the mobile bottom nav.
    const kind = this.appearanceKind( value );
    this.analyticsService.logEvent( 'Appearance Changed', {
      selection: this.appearanceLabel( value, kind ),
      kind,
      source: 'top-nav',
    } );
    if ( value === DarkLightSettings.light || value === DarkLightSettings.auto || value === DarkLightSettings.dark ) {
      // The modes are the Default row's choices, so picking one while a
      // product theme is active drops back to the default palette in that
      // mode, no forcing a light theme into dark (e.g. a "Dark Autumn").
      if ( this.currentProductKey ) {
        this.themeService.resetThemeColor();
      }
      this.themeService.setDarkLightSetting( value );
      return;
    }
    if ( value === 'default' ) {
      this.themeService.resetThemeColor();
      this.appearanceMenu?.closeDropdown();
      return;
    }
    const product = this.findThemeProduct( value );
    if ( product ) {
      this.themeService.setProductTheme( product );
    }
  }

  /** Classify an Appearance menu value for analytics: a dark/light/auto mode
   *  toggle, a reset to the default palette, or a product-theme pick. */
  private appearanceKind( value: string ): string {
    if ( value === DarkLightSettings.light || value === DarkLightSettings.auto || value === DarkLightSettings.dark ) {
      return 'mode';
    }
    return value === 'default' ? 'reset' : 'theme';
  }

  /** Human-readable label for the appearance choice, used as the Mixpanel
   *  breakdown property: 'Light' / 'Dark' / 'Auto' for modes, 'Default' for a
   *  reset, and the theme's display name (e.g. 'Aurora Borealis') for a pick. */
  private appearanceLabel( value: string, kind: string ): string {
    if ( kind === 'mode' ) {
      return value.charAt( 0 ).toUpperCase() + value.slice( 1 );
    }
    if ( kind === 'reset' ) {
      return 'Default';
    }
    // Always report the theme name in Title Case (e.g. 'Aurora Borealis',
    // 'Rio De Janeiro'), capitalising every word, falls back to the slug.
    const name = this.findThemeProduct( value )?.name || value;
    return name.replace( /[-_]+/g, ' ' ).replace( /\b\w/g, ( c ) => c.toUpperCase() );
  }

  private findThemeProduct( key: string ): Product | undefined {
    for ( const line of this.THEMES ) {
      const found = line.products.find( p => p.key === key );
      if ( found ) {
        return found;
      }
    }
    return undefined;
  }

  redirect( url: string ) {
    this.coordinator.closeOthers();
    this.routingService.redirect( url );
  }
}
